import { supabase } from '../lib/supabase';
import { auditLogger } from '../lib/auditLogger';

const IMPERSONATION_STORAGE_KEY = 'placid_impersonation_original_user_id';
const IMPERSONATION_TARGET_STORAGE_KEY = 'placid_impersonation_target_user_id';
const IMPERSONATION_REFRESH_TOKEN_KEY = 'placid_impersonation_original_refresh_token';

export interface ImpersonationTargetUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Service pour gérer l'impersonation d'utilisateur pour les superadmins
 */
export const impersonationService = {
  /**
   * Vérifie si on est actuellement en mode impersonation
   */
  isImpersonating: (): boolean => {
    return !!localStorage.getItem(IMPERSONATION_STORAGE_KEY);
  },

  /**
   * Récupère l'ID du superadmin original
   */
  getOriginalUserId: (): string | null => {
    return localStorage.getItem(IMPERSONATION_STORAGE_KEY);
  },

  /**
   * Récupère l'ID de l'utilisateur cible
   */
  getTargetUserId: (): string | null => {
    return localStorage.getItem(IMPERSONATION_TARGET_STORAGE_KEY);
  },

  /**
   * Démarre l'impersonation d'un utilisateur
   */
  impersonateUser: async (targetUserId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      // Récupérer la session actuelle pour sauvegarder l'ID du superadmin
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        return { success: false, error: 'Aucune session active' };
      }

      const originalUserId = session.user.id;

      // Appeler l'Edge Function pour générer le token d'impersonation
      const { data, error } = await supabase.functions.invoke('impersonate-user', {
        body: { targetUserId },
      });

      if (error) {
        return { success: false, error: error.message || 'Erreur lors de la génération du token d\'impersonation' };
      }

      if (!data || !data.success) {
        return { success: false, error: data?.error || 'Erreur lors de la génération du token d\'impersonation' };
      }

      // Sauvegarder le refresh token du superadmin original avant de le remplacer
      const originalRefreshToken = session.refresh_token;
      localStorage.setItem(IMPERSONATION_REFRESH_TOKEN_KEY, originalRefreshToken);

      // Utiliser le token_hash pour créer une session via verifyOtp
      if (data.tokenHash) {
        // Sauvegarder l'ID du superadmin original
        localStorage.setItem(IMPERSONATION_STORAGE_KEY, originalUserId);
        localStorage.setItem(IMPERSONATION_TARGET_STORAGE_KEY, targetUserId);

        // Utiliser verifyOtp pour créer la session
        const { data: sessionData, error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: data.tokenHash,
          type: 'magiclink',
        });

        if (verifyError || !sessionData?.session) {
          localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
          localStorage.removeItem(IMPERSONATION_TARGET_STORAGE_KEY);
          localStorage.removeItem(IMPERSONATION_REFRESH_TOKEN_KEY);
          return { success: false, error: verifyError?.message || 'Erreur lors de la création de la session' };
        }

        // Logger l'action d'impersonation
        try {
          await auditLogger.log('INSERT', 'impersonation', targetUserId, null, {
            superadminId: originalUserId,
            targetUserId: targetUserId,
            action: 'START',
          });
        } catch (auditError) {
          console.error('Error logging impersonation audit:', auditError);
          // On continue même si l'audit échoue
        }

        return { success: true };
      }

      return { success: false, error: 'Token d\'impersonation invalide' };
    } catch (error: any) {
      console.error('Error impersonating user:', error);
      return { success: false, error: error.message || 'Erreur lors de l\'impersonation' };
    }
  },

  /**
   * Arrête l'impersonation et restaure la session du superadmin original
   */
  stopImpersonation: async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const originalUserId = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
      const targetUserId = localStorage.getItem(IMPERSONATION_TARGET_STORAGE_KEY);
      const originalRefreshToken = localStorage.getItem(IMPERSONATION_REFRESH_TOKEN_KEY);

      if (!originalUserId) {
        return { success: false, error: 'Aucune impersonation active' };
      }

      // Récupérer la session actuelle pour logger
      const { data: { session } } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id;

      // Nettoyer le localStorage
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      localStorage.removeItem(IMPERSONATION_TARGET_STORAGE_KEY);
      localStorage.removeItem(IMPERSONATION_REFRESH_TOKEN_KEY);

      // Si on a le refresh token du superadmin original, l'utiliser pour restaurer la session
      if (originalRefreshToken) {
        // Récupérer un nouvel access token avec le refresh token
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession({
          refresh_token: originalRefreshToken,
        });

        if (refreshError || !refreshData?.session) {
          // Si le refresh échoue, se déconnecter
          await supabase.auth.signOut();
        } else {
          // Restaurer la session avec le nouveau token
          await supabase.auth.setSession({
            access_token: refreshData.session.access_token,
            refresh_token: refreshData.session.refresh_token,
          });
        }
      } else {
        // Si on n'a pas le refresh token, se déconnecter
        await supabase.auth.signOut();
      }

      // Logger l'action d'arrêt d'impersonation
      if (targetUserId) {
        try {
          await auditLogger.log('INSERT', 'impersonation', targetUserId, null, {
            superadminId: originalUserId,
            targetUserId: targetUserId,
            action: 'STOP',
          });
        } catch (auditError) {
          console.error('Error logging impersonation audit:', auditError);
          // On continue même si l'audit échoue
        }
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error stopping impersonation:', error);
      return { success: false, error: error.message || 'Erreur lors de l\'arrêt de l\'impersonation' };
    }
  },
};
